import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { devLog } from '@/utils/logger';

/**
 * Crisp Chat Widget Component
 *
 * Loads Crisp live chat with the native floating widget fully hidden.
 * Exposes a trigger button to be placed in the navbar.
 */

declare global {
  interface Window {
    $crisp: unknown[];
    CRISP_WEBSITE_ID: string;
  }
}

/**
 * Hook to control Crisp chat programmatically.
 * Returns { openChat, crispReady } for use in navbar or other triggers.
 */
export const useCrispChat = () => {
  const openChat = useCallback(() => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:show']);
      window.$crisp.push(['do', 'chat:open']);
    }
  }, []);

  return { openChat };
};

/** Pages where NO third-party script may load (the emailed login token is handled there). */
const isThirdPartyFreePath = (): boolean => /^\/auth\/magic\/?$/.test(window.location.pathname);

export const CrispChat = () => {
  const { user } = useAuth();
  const [crispReady, setCrispReady] = useState(false);
  // Rendered outside <BrowserRouter>, so the route is read from window.location; while blocked we
  // re-check cheaply until the SPA navigates away (e.g. after a successful magic login).
  const [blocked, setBlocked] = useState<boolean>(isThirdPartyFreePath);

  useEffect(() => {
    if (!blocked) return;
    const id = window.setInterval(() => {
      if (!isThirdPartyFreePath()) setBlocked(false);
    }, 500);
    return () => window.clearInterval(id);
  }, [blocked]);

  // Load Crisp script once the page is not a third-party-free route
  useEffect(() => {
    if (blocked) return;
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = '8b50325f-3c1e-4fa3-a1e0-282342bded7e';

    // Position on the left side
    window.$crisp.push(['config', 'position:reverse', [true]]);

    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      setTimeout(() => {
        setCrispReady(true);
        // Hide the native floating launcher — we trigger from navbar
        window.$crisp.push(['do', 'chat:hide']);
        devLog.log('[CrispChat] Crisp ready, native launcher hidden');
      }, 500);
    };

    // CSS fallback to ensure the native launcher stays hidden
    const style = document.createElement('style');
    style.id = 'crisp-custom';
    style.textContent = `
      #crisp-chatbox [data-visible="false"],
      .crisp-client .cc-kquevr,
      .crisp-client .cc-1brb6 {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleEl = document.getElementById('crisp-custom');
      if (styleEl) styleEl.remove();
    };
  }, [blocked]);

  // Re-hide launcher after chat is closed (Crisp shows it again)
  useEffect(() => {
    if (!crispReady) return;

    window.$crisp.push(['on', 'chat:closed', () => {
      window.$crisp.push(['do', 'chat:hide']);
    }]);
  }, [crispReady]);

  // Set user info when authenticated
  useEffect(() => {
    if (user && crispReady && window.$crisp) {
      try {
        window.$crisp.push(['set', 'user:nickname', [user.fullName]]);
        if (user.email) {
          window.$crisp.push(['set', 'user:email', [user.email]]);
        } else {
          devLog.log('[CrispChat] Warning: user.email is falsy, skipping email push');
        }
        if (user.team?.name) {
          window.$crisp.push(['set', 'user:company', [user.team.name]]);
        }
        // Match old-app parity: pass language so support replies in the user's language.
        // Source-of-truth is user.preferredLanguage; fall back to <html lang> set by i18n.
        const lang = user.preferredLanguage || document.documentElement.lang || 'en';
        window.$crisp.push(['set', 'user:lang', [lang]]);
        window.$crisp.push(['set', 'session:data', [
          ['user_id', user.id.toString()],
          ['team_id', user.teamId?.toString() || 'none'],
          ['package_id', user.team?.packageId?.toString() || 'none'],
        ]]);
      } catch (e) {
        devLog.log('[CrispChat] Error setting user:', e);
      }
    }
  }, [user, crispReady]);

  return null;
};
